import { useSentinelCompetitors, useSentinelHotels } from '@/api/queries'
import type { SentinelChannel, SentinelCompetitor, SentinelHotel } from '@/api/types'
import { DataTable, type Column } from '@/components/DataTable/DataTable'
import { Badge, ratioTone } from '@/components/ui/Badge'
import { Modal } from '@/components/ui/Modal'
import { compactNumber, percent } from '@/lib/format'

const successBadge = (value: number) => <Badge tone={ratioTone(value, { warnBelow: 90, dangerBelow: 70 })}>{percent(value, 1)}</Badge>

/** Hotels monitored on one review site, each expandable into its competitors. */
export function SentinelChannelDialog({ channel, onClose }: { channel: SentinelChannel; onClose: () => void }) {
  const { data, isLoading, error } = useSentinelHotels(channel.channelId)

  const columns: Column<SentinelHotel>[] = [
    { key: 'hotelName', header: 'Hotel', render: (h) => <strong>{h.hotelName || h.hotelId}</strong> },
    { key: 'hotelId', header: 'Hotel id', align: 'right' },
    { key: 'customerId', header: 'Customer id', align: 'right' },
    { key: 'totalRequests', header: 'Requests', align: 'right', render: (h) => compactNumber(h.totalRequests) },
    { key: 'successRatio', header: 'Success', align: 'right', render: (h) => successBadge(h.successRatio) },
  ]

  return (
    <Modal title={`${channel.name} · hotels (last 24h)`} onClose={onClose} size="xl">
      <DataTable
        columns={columns}
        rows={data}
        rowKey={(h) => `${h.hotelId}:${h.customerId}`}
        loading={isLoading}
        error={error}
        searchable
        searchPlaceholder="Search a hotel by name or id"
        dense
        initialSort={{ key: 'totalRequests', direction: 'desc' }}
        expandable={(h) => <Competitors channelId={channel.channelId} customerId={h.customerId} />}
      />
    </Modal>
  )
}

function Competitors({ channelId, customerId }: { channelId: number; customerId: number }) {
  const { data, isLoading, error } = useSentinelCompetitors(channelId, customerId, true)
  const columns: Column<SentinelCompetitor>[] = [
    { key: 'competitorName', header: 'Competitor', render: (c) => c.competitorName || c.competitorId },
    { key: 'competitorId', header: 'Id', align: 'right' },
    { key: 'totalRequests', header: 'Requests', align: 'right' },
    { key: 'successRatio', header: 'Success', align: 'right', render: (c) => successBadge(c.successRatio) },
  ]
  return <DataTable columns={columns} rows={data} rowKey={(c) => c.competitorId} loading={isLoading} error={error} dense maxHeight="260px" emptyMessage="No competitor scraped" />
}
